import { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Phone, Send, Loader2, Wrench, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

function generateSessionId() {
  return `chat_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

type ChatStep =
  | "welcome"
  | "repairs_postcode"
  | "repairs_postcode_input"
  | "repairs_issue"
  | "repairs_danger_warning"
  | "repairs_urgency"
  | "repairs_urgent_call"
  | "repairs_capture"
  | "repairs_capture_input"
  | "repairs_done"
  | "auto_postcode"
  | "auto_postcode_input"
  | "auto_type"
  | "auto_door"
  | "auto_capture"
  | "auto_capture_input"
  | "auto_done"
  | "not_covered";

interface ChatMessage {
  from: "bot" | "user";
  text: string;
  buttons?: { label: string; icon?: React.ReactNode; value: string; variant?: "call" | "default" }[];
}

interface LeadData {
  path: "repairs" | "automation";
  postcode: string;
  issueType?: string;
  automationType?: string;
  doorType?: string;
  urgency?: string;
  name: string;
  email: string;
  phone: string;
  description: string;
  preferredTime: string;
}

async function chatFetch(body: Record<string, unknown>) {
  return fetch(CHAT_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${ANON_KEY}` },
    body: JSON.stringify(body),
  });
}

async function checkPostcode(postcode: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.functions.invoke("check-postcode-coverage", {
      body: { postcode },
    });
    if (error) return false;
    return data?.covered === true;
  } catch {
    return false;
  }
}

const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<ChatStep>("welcome");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(generateSessionId);
  const [leadData, setLeadData] = useState<Partial<LeadData>>({});
  const [captureField, setCaptureField] = useState<"name" | "phone" | "email" | "description" | "preferredTime">("name");
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const addBot = useCallback((text: string, buttons?: ChatMessage["buttons"]) => {
    setMessages((prev) => [...prev, { from: "bot", text, buttons }]);
  }, []);

  const addUser = useCallback((text: string) => {
    setMessages((prev) => [...prev, { from: "user", text }]);
  }, []);

  // Initialize welcome
  useEffect(() => {
    if (open && messages.length === 0) {
      addBot("Hey there! 👋 Welcome to SmartLift Garage Doors.\nHow can we help you today?", [
        { label: "🔧 Repairs", value: "repairs" },
        { label: "⚡ Automation Install", value: "automation" },
        { label: "📞 Call Us Now", value: "call", variant: "call" },
      ]);
    }
  }, [open, messages.length, addBot]);

  const trackCallClick = useCallback(() => {
    chatFetch({ trackCallClick: true, sessionId }).catch(() => {});
  }, [sessionId]);

  const sendLead = useCallback(async (data: Partial<LeadData>) => {
    try {
      await chatFetch({
        sendLead: {
          name: data.name || "",
          email: data.email || "",
          phone: data.phone || "",
          postcode: data.postcode || "",
          serviceType: data.path === "repairs" 
            ? `Repair - ${data.issueType || "General"}` 
            : `Automation - ${data.automationType || "General"} (${data.doorType || "Unknown door"})`,
          description: data.description || "",
          preferredTime: data.preferredTime || "",
          urgency: data.urgency || "",
        },
        sessionId,
      });
    } catch (e) {
      console.error("Failed to send lead:", e);
    }
  }, [sessionId]);

  const logConversation = useCallback((leadCollected: boolean, lead: Partial<LeadData> | null) => {
    chatFetch({
      logData: {
        messages: messages.map((m) => ({ role: m.from === "bot" ? "assistant" : "user", content: m.text })),
        leadCollected,
        leadData: lead,
        phoneCallClicked: false,
      },
      sessionId,
    }).catch(() => {});
  }, [sessionId, messages]);

  const handleButton = useCallback((value: string) => {
    if (value === "call") {
      trackCallClick();
      window.open("tel:07836393815", "_self");
      return;
    }

    switch (step) {
      case "welcome":
        if (value === "repairs") {
          addUser("🔧 Repairs");
          setLeadData({ path: "repairs" });
          setStep("repairs_postcode");
          setTimeout(() => {
            addBot("No worries, we deal with repairs all the time! 💪\n\nPop in your postcode and I'll check we cover your area.");
            setStep("repairs_postcode_input");
          }, 400);
        } else if (value === "automation") {
          addUser("⚡ Automation Install");
          setLeadData({ path: "automation" });
          setStep("auto_postcode");
          setTimeout(() => {
            addBot("Great choice! Automation makes life so much easier 🙌\n\nWhat's your postcode? Just want to check we're in your area.");
            setStep("auto_postcode_input");
          }, 400);
        }
        break;

      case "repairs_issue":
        addUser(value);
        setLeadData((prev) => ({ ...prev, issueType: value }));
        if (value === "Broken spring" || value === "Cable snapped") {
          setStep("repairs_danger_warning");
          setTimeout(() => {
            addBot("⚠️ Please don't try to fix this yourself — broken springs and cables are really dangerous and can cause serious injury.\n\nThe safest thing is to give us a quick call and where possible we'll get to you the same day.", [
              { label: "📞 Call Us Now", value: "call", variant: "call" },
              { label: "I'd prefer a callback", value: "callback" },
            ]);
          }, 400);
        } else {
          setStep("repairs_urgency");
          setTimeout(() => {
            addBot("Got it — is this something you need sorted today, or are you happy to wait a bit?", [
              { label: "🚨 Yes – urgent!", value: "urgent" },
              { label: "Within a few days", value: "few_days" },
              { label: "Just getting a quote", value: "quote" },
            ]);
          }, 400);
        }
        break;

      case "repairs_danger_warning":
        if (value === "callback") {
          setStep("repairs_capture");
          setCaptureField("name");
          setTimeout(() => {
            addBot("No problem at all! Let me get your details and we'll arrange a callback. 😊\n\nWhat's your name?");
            setStep("repairs_capture_input");
          }, 400);
        }
        break;

      case "repairs_urgency":
        addUser(value === "urgent" ? "Yes – urgent" : value === "few_days" ? "Within a few days" : "Just getting a quote");
        setLeadData((prev) => ({ ...prev, urgency: value }));
        if (value === "urgent") {
          setStep("repairs_urgent_call");
          setTimeout(() => {
            addBot("For urgent repairs, the quickest way to get sorted is to give us a ring — where possible we'll be with you the same day! 🏃", [
              { label: "📞 Call 07836 393815", value: "call", variant: "call" },
              { label: "I'd prefer a callback", value: "callback" },
            ]);
          }, 400);
        } else {
          setStep("repairs_capture");
          setCaptureField("name");
          setTimeout(() => {
            addBot("No problem — let's get your details and one of the team will be in touch. 👍\n\nWhat's your name?");
            setStep("repairs_capture_input");
          }, 400);
        }
        break;

      case "repairs_urgent_call":
        if (value === "callback") {
          setStep("repairs_capture");
          setCaptureField("name");
          setTimeout(() => {
            addBot("Sure thing! Let me grab a few details. What's your name?");
            setStep("repairs_capture_input");
          }, 400);
        }
        break;

      case "auto_type":
        addUser(value);
        setLeadData((prev) => ({ ...prev, automationType: value }));
        setStep("auto_door");
        setTimeout(() => {
          addBot("And what type of door do you have? Don't worry if you're not sure!", [
            { label: "Up & over", value: "Up & over" },
            { label: "Sectional", value: "Sectional" },
            { label: "Roller", value: "Roller" },
            { label: "Not sure", value: "Not sure" },
          ]);
        }, 400);
        break;

      case "auto_door":
        addUser(value);
        setLeadData((prev) => ({ ...prev, doorType: value }));
        setStep("auto_capture");
        setCaptureField("name");
        setTimeout(() => {
          addBot("Lovely! Let me take a few details so we can get you a quote. 😊\n\nWhat's your name?");
          setStep("auto_capture_input");
        }, 400);
        break;
    }
  }, [step, addUser, addBot, trackCallClick, setLeadData]);

  const handleTextInput = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    addUser(text);

    // Postcode input steps
    if (step === "repairs_postcode_input" || step === "auto_postcode_input") {
      setLoading(true);
      const covered = await checkPostcode(text);
      setLoading(false);

      if (covered) {
        setLeadData((prev) => ({ ...prev, postcode: text.toUpperCase() }));
        if (step === "repairs_postcode_input") {
          setStep("repairs_issue");
          setTimeout(() => {
            addBot("Great news — we cover your area! 🎉\n\nSo what's going on with your door?", [
              { label: "Won't open", value: "Won't open" },
              { label: "Stuck halfway", value: "Stuck halfway" },
              { label: "Broken spring", value: "Broken spring" },
              { label: "Motor issue", value: "Motor issue" },
              { label: "Something else", value: "Something else" },
            ]);
          }, 400);
        } else {
          setStep("auto_type");
          setTimeout(() => {
            addBot("Brilliant — you're in our patch! 🎉\n\nWhat are you looking to do?", [
              { label: "Upgrade my manual door", value: "Manual door upgrade" },
              { label: "New motor install", value: "New motor install" },
              { label: "Not sure – need advice", value: "Need advice" },
            ]);
          }, 400);
        }
      } else {
        setStep("not_covered");
        setTimeout(() => {
          addBot("Ah, it looks like that postcode might be a bit outside our usual patch. 😕\n\nBut give us a ring anyway — we might still be able to help!", [
            { label: "📞 Call 07836 393815", value: "call", variant: "call" },
          ]);
        }, 400);
      }
      return;
    }

    // Lead capture steps
    if (step === "repairs_capture_input" || step === "auto_capture_input") {
      const isRepairs = step === "repairs_capture_input";

      switch (captureField) {
        case "name":
          setLeadData((prev) => ({ ...prev, name: text }));
          setCaptureField("phone");
          setTimeout(() => addBot(`Cheers ${text}! 👍 And what's the best number to reach you on?`), 400);
          break;
        case "phone":
          setLeadData((prev) => ({ ...prev, phone: text }));
          setCaptureField("email");
          setTimeout(() => addBot("Got it! And your email address? We'll send you a confirmation. 📧"), 400);
          break;
        case "email":
          setLeadData((prev) => ({ ...prev, email: text }));
          setCaptureField("description");
          setTimeout(() => addBot(isRepairs ? "Perfect. Can you give us a quick idea of what's going on with the door?" : "Lovely! Any details about what you're looking for? Even a rough idea helps."), 400);
          break;
        case "description":
          setLeadData((prev) => ({ ...prev, description: text }));
          setCaptureField("preferredTime");
          setTimeout(() => addBot("Nearly done! When's the best time for us to give you a bell? 📞"), 400);
          break;
        case "preferredTime": {
          const finalLead = { ...leadData, preferredTime: text, description: leadData.description || "" } as LeadData;
          setLeadData(finalLead);
          setStep(isRepairs ? "repairs_done" : "auto_done");
          setLoading(true);
          await sendLead(finalLead);
          logConversation(true, finalLead);
          setLoading(false);
          setTimeout(() => {
            addBot(
              isRepairs
                ? "That's everything — thanks a lot! ✅\n\nOne of the team will be in touch shortly. We've been doing this for 16+ years so you're in safe hands! 💪\n\nIf you can't wait, give us a ring anytime."
                : "Brilliant, all noted! ✅\n\nWe'll put together a tailored quote and get back to you. No obligation, completely free! 😊\n\nOr if you'd rather chat it through, give us a call anytime.",
              [{ label: "📞 Call 07836 393815", value: "call", variant: "call" }]
            );
          }, 400);
          break;
        }
      }
      return;
    }
  }, [input, loading, step, captureField, leadData, addUser, addBot, sendLead, logConversation]);

  // Determine if text input is active
  const showTextInput =
    step === "repairs_postcode_input" ||
    step === "auto_postcode_input" ||
    step === "repairs_capture_input" ||
    step === "auto_capture_input";

  const inputPlaceholder = (() => {
    if (step.includes("postcode")) return "Enter your postcode...";
    switch (captureField) {
      case "name": return "Your name...";
      case "phone": return "Your phone number...";
      case "email": return "Your email address...";
      case "description": return "Brief description...";
      case "preferredTime": return "e.g. Morning, Afternoon...";
      default: return "Type a message...";
    }
  })();

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-lg hover:scale-105 transition-transform animate-bounce"
          style={{ animationDuration: "2s", animationIterationCount: "3" }}
          aria-label="Open chat"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div
          className="fixed bottom-4 right-4 z-50 flex w-[370px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-2xl sm:bottom-6 sm:right-6"
          style={{ height: "min(540px, calc(100vh - 2rem))" }}
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-2 bg-accent px-4 py-3 text-accent-foreground">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              <div>
                <span className="font-semibold text-sm block leading-tight">SmartLift Assistant</span>
                <span className="text-[10px] opacity-80">Typically replies instantly</span>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="rounded-md p-1 hover:bg-accent-foreground/20 transition-colors"
              aria-label="Close chat"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className="space-y-2">
                <div
                  className={cn(
                    "max-w-[88%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap",
                    m.from === "bot"
                      ? "bg-muted text-foreground rounded-bl-md"
                      : "bg-accent text-accent-foreground rounded-br-md ml-auto"
                  )}
                >
                  {m.text.split(/(\*\*.*?\*\*)/g).map((part, j) =>
                    part.startsWith("**") && part.endsWith("**") ? (
                      <strong key={j}>{part.slice(2, -2)}</strong>
                    ) : (
                      <span key={j}>{part}</span>
                    )
                  )}
                </div>
                {/* Buttons */}
                {m.buttons && i === messages.length - 1 && (
                  <div className="flex flex-wrap gap-2 pl-1">
                    {m.buttons.map((btn) => (
                      <button
                        key={btn.value}
                        onClick={() => handleButton(btn.value)}
                        className={cn(
                          "rounded-lg px-3 py-2 text-sm font-medium transition-all active:scale-95",
                          btn.variant === "call"
                            ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm"
                            : "border border-border bg-background text-foreground hover:bg-muted hover:border-accent"
                        )}
                      >
                        {btn.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                {step.includes("postcode") ? "Checking coverage..." : "Sending..."}
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Persistent call banner */}
          <a
            href="tel:07836393815"
            onClick={trackCallClick}
            className="mx-3 mb-2 flex items-center justify-center gap-2 rounded-lg bg-accent py-2.5 text-accent-foreground text-sm font-semibold hover:bg-accent/90 transition-colors shadow-sm"
          >
            <Phone className="h-4 w-4" /> Call 07836 393815
          </a>

          {/* Text input - only shown when needed */}
          {showTextInput && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleTextInput();
              }}
              className="flex items-center gap-2 border-t border-border px-3 py-2"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={inputPlaceholder}
                className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
                disabled={loading}
                maxLength={200}
                autoFocus
              />
              <Button
                type="submit"
                size="icon"
                variant="ghost"
                disabled={!input.trim() || loading}
                className="h-8 w-8 shrink-0"
              >
                <Send className="h-4 w-4" />
              </Button>
            </form>
          )}
        </div>
      )}
    </>
  );
};

export default Chatbot;
